import { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Link, NavLink, useLocation, useMatch } from 'react-router';
import { rubMapsUrl } from '@shared/rub-maps';
import { useVenuesEnabled } from '@/lib/use-venues-enabled';
import Icon from '@/components/Icon';
import FindSheet from '@/components/FindSheet';
import Legend from '@/components/Legend';
import PhotoViewer from '@/components/PhotoViewer';
import { useLocations } from '@/stores/locations';
import { useSession } from '@/stores/session';
import { isSignedIn, usePunter } from '@/stores/punter';
import { useUi } from '@/stores/ui';
import wordmark from '../../assets/brand/logo/wordmark-light.svg';

// The pages. Search is not a page — it is the find sheet — but it is named here as well as behind
// the header's magnifying glass, because an icon alone is easy to miss. The city pages keep their
// own places: the rail, and the find sheet.
const BROWSE = [
  { to: '/', label: 'Home' },
  { to: '/browse', label: 'All Australia' },
  // `shuffle` deals a new order on arrival; Back to this entry keeps the one it dealt.
  { to: '/gallery', label: 'Random gallery', state: { shuffle: true } },
  { search: true, label: 'Search' },
  { to: '/advertise', label: 'Advertise with us' },
] as const;

type MenuItem = (typeof BROWSE)[number];

const ABOUT = [
  { to: '/terms', label: 'Terms' },
  { to: '/privacy', label: 'Privacy' },
];

function DrawerLink({
  to,
  label,
  state,
  onNavigate,
}: {
  to: string;
  label: string;
  state?: unknown;
  onNavigate: () => void;
}) {
  return (
    <NavLink className="ee-drawer__link" to={to} state={state} end onClick={onNavigate} data-ee-feature="nav.link">
      {label}
      <Icon name="chevron-right" size={16} className="ee-drawer__chevron" />
    </NavLink>
  );
}

/** A menu row that is a page, or the one that opens the find sheet. */
function DrawerItem({
  item,
  onNavigate,
  onSearch,
}: {
  item: MenuItem;
  onNavigate: () => void;
  onSearch: () => void;
}) {
  if ('search' in item) {
    return (
      <button type="button" className="ee-drawer__link" onClick={onSearch} data-ee-feature="nav.search">
        {item.label}
        <Icon name="search" size={16} className="ee-drawer__chevron" />
      </button>
    );
  }
  return <DrawerLink {...item} onNavigate={onNavigate} />;
}

/**
 * The external Rub maps site, while the site's own Venues map is switched off
 * (`venues.enabled`). On a city page it opens that place's map. A plain anchor, because it
 * leaves the site; it opens in a new tab so the reader's place here is kept.
 */
function RubMapsLink({ className, feature, place }: { className: string; feature: string; place: string | null }) {
  return (
    <a
      className={className}
      href={rubMapsUrl(place)}
      target="_blank"
      rel="noopener noreferrer"
      data-ee-feature={feature}
    >
      Rub maps
      <span className="ee-visually-hidden"> (opens another site)</span>
      <Icon name="external" size={16} className="ee-drawer__chevron" />
    </a>
  );
}

/**
 * The same destinations as the drawer, in the bar itself from `lg`. The two carry different
 * feature names on purpose: a visitor who opened a menu and a visitor who clicked a bar did not
 * do the same thing, and one name for both reports them as one. `nav.link` keeps its existing
 * meaning, so no series already in the warehouse is broken by this.
 */
function BarLink({ to, label, state }: { to: string; label: string; state?: unknown }) {
  return (
    <NavLink className="ee-headernav__link" to={to} state={state} end data-ee-feature="nav.bar">
      {label}
    </NavLink>
  );
}

function BarItem({ item, onSearch }: { item: MenuItem | (typeof ABOUT)[number]; onSearch: () => void }) {
  if ('search' in item) {
    return (
      <button type="button" className="ee-headernav__link" onClick={onSearch} data-ee-feature="nav.bar.search">
        {item.label}
      </button>
    );
  }
  return <BarLink {...item} />;
}

// The one place the layout is a fact in JavaScript rather than in a stylesheet, because two
// things here cannot be done in CSS. A drawer left open across the boundary loses the button
// that opened it, and Radix returns focus to that button on close — a `display: none` trigger
// drops focus to <body>. And the bar states who you are, so the session has to be known before
// anything has been opened. Everything else about the desktop layout is a media query.
const WIDE = `(min-width: 1120px)`;

function useWide(): boolean {
  const [wide, setWide] = useState(() => window.matchMedia(WIDE).matches);

  useEffect(() => {
    const query = window.matchMedia(WIDE);
    const onChange = (event: MediaQueryListEvent) => setWide(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return wide;
}

/**
 * The chrome every public route sits inside: a slim sticky header, the drawer behind it, and
 * one footer. Putting it here rather than in each page is what makes the header a constant
 * instead of a thing each screen re-invents.
 *
 * The admin portal is deliberately excluded. Nothing links to it, so it must not wear
 * navigation that does.
 */
export default function AppShell({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const findOpen = useUi((state) => state.findOpen);
  const setFindOpen = useUi((state) => state.setFindOpen);
  const { locations, load } = useLocations();
  const user = useSession((state) => state.user);
  const sessionStatus = useSession((state) => state.status);
  const loadSession = useSession((state) => state.load);
  const punterSignedIn = usePunter(isSignedIn);
  const punterStatus = usePunter((state) => state.status);
  const loadPunter = usePunter((state) => state.load);
  // While the Venues map is off, the maps are another site's, linked from the menu and footer.
  const rubMapsOut = !useVenuesEnabled();
  const cityPlace = useMatch('/c/:city')?.params.city ?? null;

  const wide = useWide();
  const main = useRef<HTMLElement>(null);
  const footer = useRef<HTMLElement>(null);
  const firstPath = useRef(true);

  // A route change in a single-page app is silent: the old page's focus survives into the new
  // one, and a screen reader is told nothing. Focus goes to the content instead, without moving
  // the scroll — the grid anchor and the page-top reset own that. Not on the first page drawn,
  // where the browser has already put the reader at the top.
  useEffect(() => {
    if (firstPath.current) {
      firstPath.current = false;
      return;
    }
    main.current?.focus({ preventScroll: true });
  }, [pathname]);

  // The drawer is the phone's navigation. Above `lg` its links are in the bar and its trigger
  // is gone, so an open drawer carried across the boundary would have nothing to close back to.
  useEffect(() => {
    if (wide) setMenuOpen(false);
  }, [wide]);

  // The city list is not needed to render the grid, so it waits for something that shows it.
  useEffect(() => {
    if (!menuOpen && !findOpen) return;
    void load();
  }, [menuOpen, findOpen, load]);

  // The footer's key prints the colour packs, which ride on the same response. It asks for them
  // as the footer comes near the screen rather than on first paint, for the same reason.
  useEffect(() => {
    const node = footer.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        void load();
      },
      { rootMargin: '400px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [load, pathname]);

  // The session is fetched from `lg`, because the bar names the account. On a phone it still
  // waits: the first screen spends its requests on listings.
  useEffect(() => {
    if (!wide && !menuOpen && !findOpen) return;
    if (sessionStatus === 'idle') void loadSession().catch(() => undefined);
    if (punterStatus === 'idle') void loadPunter().catch(() => undefined);
  }, [wide, menuOpen, findOpen, sessionStatus, loadSession, punterStatus, loadPunter]);

  if (pathname.startsWith('/x/')) return <>{children}</>;

  const account = user
    ? { to: user.role === 'agent' ? '/agency' : '/me', label: 'Your dashboard' }
    : { to: '/login', label: 'Advertiser sign in' };
  // A punter's own account: their saved lists, or the way in. Separate from the advertiser's
  // account above: one browser may hold both, and each row says which it is.
  const lists = punterSignedIn
    ? { to: '/lists', label: 'Your lists' }
    : { to: '/signin', label: 'Sign in or make an account' };
  // The bar has room for one account link: whichever account is signed in, else the punter's
  // way in, whose page links on to the advertiser's.
  const barAccount = user ? account : punterSignedIn ? lists : { to: '/signin', label: 'Sign in' };

  return (
    <div className="ee-shell">
      {/* First in the document, and visible only when focused: past it a keyboard has the menu,
          the bar and a dozen rail chips to tab through before the first card. */}
      <a className="ee-skip" href="#content" data-ee-feature="nav.skip">
        Skip to content
      </a>

      <header className="ee-header">
        <Dialog.Root open={menuOpen} onOpenChange={setMenuOpen}>
          <Dialog.Trigger
            className="ee-header__icon ee-header__icon--menu"
            aria-label="Open menu"
            data-ee-feature="nav.open"
          >
            <Icon name="menu" />
          </Dialog.Trigger>

          <Dialog.Portal>
            <Dialog.Overlay className="ee-scrim" />
            <Dialog.Content className="ee-drawer" aria-describedby={undefined}>
              <div className="ee-drawer__head">
                <Dialog.Title className="ee-visually-hidden">Menu</Dialog.Title>
                <img
                  className="ee-drawer__wordmark"
                  src={wordmark}
                  alt="EscortEssential"
                  width={320}
                  height={60}
                />
                <Dialog.Close className="ee-header__icon" aria-label="Close menu" data-ee-feature="nav.close">
                  <Icon name="close" />
                </Dialog.Close>
              </div>

              <nav className="ee-drawer__group" aria-label="Browse">
                <p className="ee-drawer__eyebrow">Browse</p>
                {BROWSE.map((item) => (
                  <DrawerItem
                    key={item.label}
                    item={item}
                    onNavigate={() => setMenuOpen(false)}
                    onSearch={() => {
                      setMenuOpen(false);
                      setFindOpen(true);
                    }}
                  />
                ))}
                {rubMapsOut ? (
                  <RubMapsLink className="ee-drawer__link" feature="nav.rubmaps" place={cityPlace} />
                ) : (
                  <DrawerLink to="/venues" label="Towel maps" onNavigate={() => setMenuOpen(false)} />
                )}
              </nav>

              <nav className="ee-drawer__group" aria-label="Account">
                <p className="ee-drawer__eyebrow">Account</p>
                <DrawerLink {...lists} onNavigate={() => setMenuOpen(false)} />
                <DrawerLink {...account} onNavigate={() => setMenuOpen(false)} />
              </nav>

              <nav className="ee-drawer__group" aria-label="About">
                <p className="ee-drawer__eyebrow">About</p>
                {ABOUT.map((item) => (
                  <DrawerLink key={item.to} {...item} onNavigate={() => setMenuOpen(false)} />
                ))}
              </nav>

              <Legend variant="drawer" />
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        <Link className="ee-header__wordmark" to="/" data-ee-feature="nav.home">
          <img src={wordmark} alt="EscortEssential" width={320} height={60} />
        </Link>

        <nav className="ee-headernav" aria-label="Sections">
          {[...BROWSE, ...ABOUT].map((item) => (
            <BarItem key={item.label} item={item} onSearch={() => setFindOpen(true)} />
          ))}
        </nav>

        <Link className="ee-header__account" to={barAccount.to} data-ee-feature="nav.account">
          {barAccount.label}
        </Link>

        {/* Not on the homepage: its own Search action is one thumb's reach below, and two ways
            into one sheet on one screen is one too many. The column stays, so the mark stays
            centred. */}
        {pathname === '/' ? null : (
          <button
            type="button"
            className="ee-header__icon ee-header__icon--end"
            aria-label="Find someone"
            onClick={() => setFindOpen(true)}
            data-ee-feature="nav.find"
          >
            <Icon name="search" />
          </button>
        )}
      </header>

      <FindSheet open={findOpen} onOpenChange={setFindOpen} locations={locations} />
      {/* Mounted once, opened from the feed and from any profile gallery. */}
      <PhotoViewer />

      <main className="ee-main" id="content" tabIndex={-1} ref={main}>
        {children}
      </main>

      <footer className="ee-footer" ref={footer}>
        <Legend variant="footer" />

        <nav className="ee-footer__links" aria-label="Site">
          <Link className="ee-footer__link" to="/advertise" data-ee-feature="footer.advertise">
            Advertise with us
          </Link>
          <Link className="ee-footer__link" to="/terms" data-ee-feature="footer.terms">
            Terms
          </Link>
          <Link className="ee-footer__link" to="/privacy" data-ee-feature="footer.privacy">
            Privacy
          </Link>
          <Link className="ee-footer__link" to={lists.to} data-ee-feature="footer.lists">
            {lists.label}
          </Link>
          <Link className="ee-footer__link" to={account.to} data-ee-feature="footer.account">
            {account.label}
          </Link>
          {/* Desktop only: below `lg` the drawer carries it. */}
          {rubMapsOut ? (
            <RubMapsLink
              className="ee-footer__link ee-footer__link--desk"
              feature="footer.rubmaps"
              place={cityPlace}
            />
          ) : null}
        </nav>

        <p className="ee-footer__fine">
          Adults only. Everyone advertising here has passed an in-person identity and age check.
          Payment and verification happen in person, never through this site.
        </p>
        <p className="ee-footer__fine">
          © {new Date().getFullYear()} EscortEssential Pty Ltd · ACN 139 606 429
        </p>
      </footer>
    </div>
  );
}
